import { PaymentStatus, LifecycleStatus, QuotationStatus } from '@typings'

// Lifecycle status labels (draft/finalized)
export const LIFECYCLE_STATUS_LABELS: Record<LifecycleStatus, string> = {
  draft: 'Concept',
  finalized: 'Definitief',
}

// Lifecycle status colors
export const LIFECYCLE_STATUS_COLORS: Record<
  LifecycleStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  draft: 'default',
  finalized: 'info',
}

// Payment status labels (for finalized invoices)
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: 'Niet verzonden',
  sent: 'Verzonden',
  paid: 'Betaald',
  overdue: 'Verlopen',
  credited: 'Gecrediteerd',
}

// Payment status colors
export const PAYMENT_STATUS_COLORS: Record<
  PaymentStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  unpaid: 'default',
  sent: 'primary',
  paid: 'success',
  overdue: 'error',
  credited: 'warning',
}

// Lifecycle status tooltips
export const LIFECYCLE_STATUS_TOOLTIPS: Record<LifecycleStatus, string> = {
  draft: 'Dit document is nog in concept en kan worden bewerkt',
  finalized: 'Dit document is definitief gemaakt en kan niet meer worden gewijzigd',
}

// Payment status tooltips
export const PAYMENT_STATUS_TOOLTIPS: Record<PaymentStatus, string> = {
  unpaid: 'Deze factuur is nog niet verzonden naar de klant',
  sent: 'Deze factuur is verzonden naar de klant en wacht op betaling',
  paid: 'Deze factuur is volledig betaald',
  overdue: 'De betalingstermijn van deze factuur is verlopen',
  credited: 'Deze factuur is gecrediteerd',
}

// Payment status options for dropdowns
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Niet verzonden' },
  { value: 'sent', label: 'Verzonden' },
  { value: 'paid', label: 'Betaald' },
  { value: 'overdue', label: 'Verlopen' },
] as const

// Payment status transitions (what statuses can be changed to)
export const PAYMENT_STATUS_TRANSITIONS: Record<PaymentStatus, PaymentStatus[]> = {
  unpaid: ['sent', 'paid', 'overdue'],
  sent: ['paid', 'overdue'],
  paid: [], // Cannot change from paid
  overdue: ['paid'],
  credited: [], // Cannot change from credited
}

// Quotation status labels
export const QUOTATION_STATUS_LABELS: Record<QuotationStatus, string> = {
  not_sent: 'Niet verzonden',
  sent: 'Verzonden',
  accepted: 'Geaccepteerd',
  rejected: 'Geweigerd',
  invoiced: 'Gefactureerd',
}

// Quotation status colors
export const QUOTATION_STATUS_COLORS: Record<
  QuotationStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  not_sent: 'default',
  sent: 'primary',
  accepted: 'success',
  rejected: 'error',
  invoiced: 'info',
}

// Quotation status tooltips
export const QUOTATION_STATUS_TOOLTIPS: Record<QuotationStatus, string> = {
  not_sent: 'Deze offerte is nog niet verzonden naar de klant',
  sent: 'Deze offerte is verzonden naar de klant en wacht op een reactie',
  accepted: 'Deze offerte is geaccepteerd door de klant',
  rejected: 'Deze offerte is geweigerd door de klant',
  invoiced: 'Er is een factuur aangemaakt voor deze offerte',
}

// Quotation status options for dropdowns
export const QUOTATION_STATUS_OPTIONS = [
  { value: 'not_sent', label: 'Niet verzonden' },
  { value: 'sent', label: 'Verzonden' },
  { value: 'accepted', label: 'Geaccepteerd' },
  { value: 'rejected', label: 'Geweigerd' },
  { value: 'invoiced', label: 'Gefactureerd' },
] as const

// Quotation status transitions (forward-only)
export const QUOTATION_STATUS_TRANSITIONS: Record<QuotationStatus, QuotationStatus[]> = {
  not_sent: ['sent'],
  sent: ['accepted', 'rejected', 'invoiced'],
  accepted: ['invoiced'],
  rejected: [], // Cannot transition from rejected
  invoiced: [], // Cannot transition from invoiced
}

// Statuses that require a comment
export const QUOTATION_STATUSES_REQUIRING_COMMENT: QuotationStatus[] = ['accepted', 'rejected']
